/** Canonical hosted PaperTiger MCP endpoint used by supported AI clients. */
export const PAPER_TIGER_MCP_URL =
  "https://api.papertigerapp.com/mcp";

/** One user-facing group of related PaperTiger MCP capabilities. */
export interface PaperTigerCapabilityGroup {
  /** Human-readable capability group title. */
  title: string;
  /** Short explanation of the outcome this group supports. */
  description: string;
  /** Exact MCP tool names included in the group. */
  tools: readonly string[];
}

/** Complete user-facing inventory of the hosted PaperTiger MCP tools. */
export const PAPER_TIGER_CAPABILITY_GROUPS: readonly PaperTigerCapabilityGroup[] =
  [
    {
      title: "Account orientation",
      description:
        "Inspect service capabilities, connected account context, wallets, buying power, exposure, and wallet limits.",
      tools: ["get_capabilities", "get_account_context"],
    },
    {
      title: "Virtual wallets",
      description:
        "Create and rename isolated paper wallets, or explicitly reset and delete a confirmed wallet.",
      tools: [
        "create_wallet",
        "update_wallet",
        "reset_wallet",
        "delete_wallet",
      ],
    },
    {
      title: "Market discovery and data quality",
      description:
        "Search current Polymarket and Kalshi markets, resolve exact venue URLs, and inspect causal historical coverage and gaps.",
      tools: ["search_markets", "resolve_market", "get_data_quality"],
    },
    {
      title: "Experiment design and execution",
      description:
        "Record hypotheses and guardrails, create immutable strategy versions, and run historical backtests.",
      tools: [
        "create_experiment",
        "create_strategy_version",
        "start_backtest",
      ],
    },
    {
      title: "Run results",
      description:
        "Inspect deterministic historical and external-paper run status without changing the run.",
      tools: ["get_run_status"],
    },
    {
      title: "Paper execution",
      description:
        "Place simulated orders using canonical market identifiers and cancel open paper orders. No tool can place a real-money venue order.",
      tools: ["place_paper_order", "cancel_paper_order"],
    },
    {
      title: "Analysis and reproducibility",
      description:
        "Explain fills and fees, analyze deterministic economics, diagnose rejection funnels, compare compatible runs, and export bounded research bundles.",
      tools: [
        "explain_trade",
        "analyze_run",
        "get_rejection_funnel",
        "compare_runs",
        "export_research_bundle",
      ],
    },
  ];

/**
 * Public guide for connecting and using PaperTiger from supported AI clients.
 *
 * @returns Installation, authentication, capability, safety, and troubleshooting documentation.
 */
export function IntegrationsGuide(): React.JSX.Element {
  return (
    <main className="integrations-shell">
      <header className="integrations-heading">
        <p className="eyebrow">AI integrations</p>
        <h1>Use PaperTiger from ChatGPT, Codex, or Claude</h1>
        <p>
          PaperTiger runs one hosted, authenticated MCP service. ChatGPT and
          Codex package it with a PaperTiger research skill; Claude connects to
          the same tools as a custom connector. Nothing runs on your computer.
        </p>
      </header>

      <section className="integration-status surface" aria-labelledby="availability">
        <div>
          <p className="eyebrow">Availability</p>
          <h2 id="availability">What you can connect today</h2>
        </div>
        <div className="integration-status-grid">
          <article>
            <span className="status-chip pending">Review pending</span>
            <h3>ChatGPT and Codex plugin</h3>
            <p>
              The production service is ready, but the public Plugins Directory
              listing is not available until OpenAI approves and publishes it.
              Once listed, one install enables the MCP tools and bundled
              PaperTiger research workflow.
            </p>
          </article>
          <article>
            <span className="status-chip available">Available now</span>
            <h3>Claude custom connector</h3>
            <p>
              Claude Free, Pro, Max, Team, and Enterprise users can add the
              production MCP URL now. Free accounts are limited to one custom
              connector; Team and Enterprise owners must add organization
              connectors first.
            </p>
          </article>
        </div>
      </section>

      <section className="integration-section" id="chatgpt">
        <div className="integration-section-heading">
          <p className="eyebrow">ChatGPT and Codex</p>
          <h2>Install the PaperTiger plugin</h2>
          <p>
            These steps become available when PaperTiger is published in the
            universal Plugins Directory shared by ChatGPT and Codex.
          </p>
        </div>
        <ol className="integration-steps">
          <li>
            <strong>Create your PaperTiger account.</strong>
            <span>
              Register on this site and confirm your email before connecting an
              AI client.
            </span>
          </li>
          <li>
            <strong>Open a supported Plugins surface.</strong>
            <span>
              On ChatGPT web, turn on Work and open Plugins. In the ChatGPT
              desktop app, use ChatGPT Work or Codex and open Plugins.
            </span>
          </li>
          <li>
            <strong>Search for PaperTiger and install it.</strong>
            <span>
              Open the listing and select the plus button. Plugins are not
              currently available in normal Chat, the IDE extension, or mobile.
            </span>
          </li>
          <li>
            <strong>Connect your PaperTiger account.</strong>
            <span>
              Select Connect when prompted. PaperTiger opens its own sign-in and
              consent page; sign in and approve the account-level paper-trading
              grant.
            </span>
          </li>
          <li>
            <strong>Start a new chat.</strong>
            <span>
              Ask for a PaperTiger outcome directly, or type <code>@</code> and
              select PaperTiger when you want to invoke it explicitly.
            </span>
          </li>
        </ol>
        <p className="integration-source-note">
          Current client instructions:{" "}
          <a href="https://learn.chatgpt.com/docs/plugins">
            OpenAI Plugins documentation
          </a>.
        </p>
      </section>

      <section className="integration-section" id="claude">
        <div className="integration-section-heading">
          <p className="eyebrow">Claude</p>
          <h2>Add PaperTiger as a custom connector</h2>
          <p>
            Anthropic calls hosted MCP integrations “custom connectors.” No
            local Claude Desktop configuration file is needed.
          </p>
        </div>
        <ol className="integration-steps">
          <li>
            <strong>Open the appropriate Connectors settings.</strong>
            <span>
              Free, Pro, and Max users open Customize → Connectors. On Team or
              Enterprise, an Owner or Primary Owner first opens Organization
              settings → Connectors.
            </span>
          </li>
          <li>
            <strong>Add a custom web connector.</strong>
            <span>
              Individual accounts select +, then Add custom connector. Team and
              Enterprise owners select Add, then Custom → Web. Name it
              “PaperTiger.”
            </span>
          </li>
          <li>
            <strong>Enter the production MCP URL.</strong>
            <code className="integration-url">{PAPER_TIGER_MCP_URL}</code>
          </li>
          <li>
            <strong>Select Add, then Connect.</strong>
            <span>
              PaperTiger opens its own OAuth page. Sign in with your verified
              PaperTiger account and approve the account-level paper-trading
              grant.
            </span>
          </li>
          <li>
            <strong>Enable PaperTiger for the conversation.</strong>
            <span>
              Select + in the lower-left of the chat, open Connectors, and
              enable PaperTiger. Enable only the tools relevant to the
              conversation.
            </span>
          </li>
        </ol>
        <p className="integration-source-note">
          Current client instructions:{" "}
          <a href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp">
            Anthropic custom connector documentation
          </a>.
        </p>
      </section>

      <section className="integration-section" id="authentication">
        <div className="integration-section-heading">
          <p className="eyebrow">Authentication</p>
          <h2>What the connection authorizes</h2>
        </div>
        <div className="integration-explainer-grid">
          <article className="surface">
            <h3>Your password stays with PaperTiger</h3>
            <p>
              Authentication uses authorization code with PKCE. You enter your
              email and password only on PaperTiger’s OAuth page. ChatGPT,
              Codex, and Claude receive revocable tokens, not your password or a
              PaperTiger API key.
            </p>
          </article>
          <article className="surface">
            <h3>One full paper-account grant</h3>
            <p>
              The <code>papertiger:account</code> scope can read and modify all
              virtual wallets, experiments, strategy versions, runs, and paper
              orders in the connected account. It cannot submit real-money
              orders.
            </p>
          </article>
          <article className="surface">
            <h3>Short-lived, revocable access</h3>
            <p>
              Access tokens expire after one hour. Refresh tokens rotate for up
              to 30 days, and disconnecting PaperTiger revokes the connection.
              Reset and delete actions require the exact wallet name.
            </p>
          </article>
        </div>
      </section>

      <section className="integration-section" id="capabilities">
        <div className="integration-section-heading">
          <p className="eyebrow">Capabilities</p>
          <h2>Everything the hosted connection can do</h2>
          <p>
            The server exposes 24 goal-oriented tools. Every supported client
            receives the same tool contracts and safety annotations.
          </p>
        </div>
        <div className="integration-capability-grid">
          {PAPER_TIGER_CAPABILITY_GROUPS.map((group) => (
            <article className="surface" key={group.title}>
              <h3>{group.title}</h3>
              <p>{group.description}</p>
              <ul aria-label={`${group.title} tools`}>
                {group.tools.map((tool) => (
                  <li key={tool}><code>{tool}</code></li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="integration-section" id="examples">
        <div className="integration-section-heading">
          <p className="eyebrow">Example prompts</p>
          <h2>Start with an outcome</h2>
        </div>
        <div className="integration-prompt-list">
          <blockquote>
            Show my PaperTiger wallets, buying power, open positions, and
            current exposure. Do not change anything.
          </blockquote>
          <blockquote>
            Create separate $1,000 paper wallets for two BTC five-minute
            hypotheses, record their success criteria, and do not start a run
            until I approve the strategy versions.
          </blockquote>
          <blockquote>
            Check causal data coverage, backtest these two immutable strategy
            versions on the same interval, and compare their rejection funnels,
            fees, and net results.
          </blockquote>
          <blockquote>
            Explain why this run produced no fills. Separate unavailable
            signals, rejected candidates, submitted orders, and execution
            failures.
          </blockquote>
        </div>
      </section>

      <section className="integration-section" id="limits">
        <div className="integration-section-heading">
          <p className="eyebrow">Safety and limitations</p>
          <h2>Interpret the results correctly</h2>
        </div>
        <ul className="integration-limit-list">
          <li>
            PaperTiger is for users aged 13 and older. Users under their local
            age of majority need permission from a parent or legal guardian.
          </li>
          <li>Every order and wallet balance is simulated.</li>
          <li>
            Historical results depend on point-in-time coverage; sampled or
            gapped intervals are reported and must not be treated as continuous.
          </li>
          <li>
            Managed forward runs fail closed for signals or hard gates that need
            unavailable continuous event, trade, or reference history.
          </li>
          <li>
            Paper results are research evidence, not proof of live-trading
            readiness or future profitability.
          </li>
          <li>
            PaperTiger does not provide personalized financial or gambling
            advice and must not be used to facilitate real-money transactions.
          </li>
          <li>
            Review every write request, especially wallet reset, wallet delete,
            and paper-order placement.
          </li>
        </ul>
      </section>

      <section className="integration-section" id="troubleshooting">
        <div className="integration-section-heading">
          <p className="eyebrow">Troubleshooting</p>
          <h2>If PaperTiger does not appear or connect</h2>
        </div>
        <dl className="integration-troubleshooting">
          <div>
            <dt>ChatGPT or Codex cannot find PaperTiger</dt>
            <dd>
              The public listing is still pending OpenAI approval. After
              publication, use ChatGPT Work, Codex in the desktop app, or the
              supported Codex plugin browser—not normal Chat, mobile, or the IDE
              extension.
            </dd>
          </div>
          <div>
            <dt>Claude does not show Add custom connector</dt>
            <dd>
              Free accounts can add one custom connector. For Team and
              Enterprise, ask an Owner or Primary Owner to add PaperTiger under
              Organization settings → Connectors first.
            </dd>
          </div>
          <div>
            <dt>PaperTiger rejects sign-in</dt>
            <dd>
              Confirm the account email first, then retry Connect. Use{" "}
              <a href="/support">Support</a> if the account is still blocked.
            </dd>
          </div>
          <div>
            <dt>You no longer want the connection</dt>
            <dd>
              Remove or disconnect PaperTiger in the client’s Plugins or
              Connectors settings. PaperTiger supports OAuth token revocation.
            </dd>
          </div>
        </dl>
        <div className="integration-actions">
          <a className="primary link-button" href="/#join">Create PaperTiger account</a>
          <a className="secondary link-button" href="/mcp.md">MCP technical guide</a>
          <a className="secondary link-button" href="/docs">API documentation</a>
        </div>
      </section>
    </main>
  );
}
